import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/message';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {CalendarsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarVersionModel} from '../../../../models/api/calendarVersionModel';
import {ViewingNowCalendar} from '../../../../store/panels/calendars/calendarsPanelState';
import {FindCalendarDropdownBoxContainer} from '../../findCalendar/findCalendarDropdownBoxContainer';
import {DoubleBookingCalendarsListItem} from './doubleBookingCalendarsListItem';
import {MyCalendarsDropdownContainer} from './myCalendarsDropdownContainer';

/*
 * Types.
 */

export interface DoubleBookingCalendarData {
  viewingNowCalendar: ViewingNowCalendar;
  onRemove: (calendar: ViewingNowCalendar) => void;
  name?: string;
}

/*
 * Props.
 */

interface DoubleBookingCalendarsDropdownProps {
  panelId: CalendarsPanelId;
  calendarVersions: ResourceMap<CalendarVersionModel>;
  doubleBookingCalendars: ReadonlyArray<DoubleBookingCalendarData>;
  width: number;
}

/*
 * Style.
 */

const StyledCalendarListWrapper = styled.div`
  margin-top: 10px;
`;

/*
 * Component.
 */

export const DoubleBookingCalendarsDropdown: FC<DoubleBookingCalendarsDropdownProps> = (props) => {
  const theme = useUserTheme();
  const localize = useLocalize();

  const {doubleBookingCalendars} = props;
  const hasDoubleBookingCalendars = doubleBookingCalendars.length > 0;

  return (
    <>
      <FindCalendarDropdownBoxContainer
        panelId={props.panelId}
        inputBackgroundColor={theme.greys.shade20}
        placeholder={localize(
          defineMessage({
            id: 'schedulinglink-settings-add-calendar',
            message: 'Search for calendars',
          }),
        )}
        noMargin
        width={props.width}
        renderDefaultDropdown={(dropdownProps) => renderDefaultDropdown(props, dropdownProps)}
      />
      {hasDoubleBookingCalendars && (
        <StyledCalendarListWrapper>
          {doubleBookingCalendars.map((doubleBookingCalendar) => (
            <DoubleBookingCalendarsListItem
              key={doubleBookingCalendar.viewingNowCalendar.id}
              doubleBookingCalendar={doubleBookingCalendar.viewingNowCalendar}
              onRemoveDoubleBookingCalendar={doubleBookingCalendar.onRemove}
              nameOverride={doubleBookingCalendar.name}
            />
          ))}
        </StyledCalendarListWrapper>
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderDefaultDropdown(props: DoubleBookingCalendarsDropdownProps, dropdownProps: DropdownProps) {
  return <MyCalendarsDropdownContainer {...dropdownProps} maxHeight={315} panelId={props.panelId} />;
}
